import * as React from "react";
import clsx from "clsx";
import { Loader2, Pin } from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "@/components/ui/button";
import {
  useAddSnapshotViewFavoriteMutation,
  useDeleteSnapshotViewFavoriteMutation,
  useGetSnapshotViewFavoritesQuery,
} from "@/modules/snapshots/snapshotViews.api";
import type { SnapshotView } from "@/modules/snapshots/snapshotViews.types";

interface Props {
  view: SnapshotView;
  compact?: boolean;
}

export const SnapshotViewPinButton = ({ view, compact = false }: Props) => {
  const { data: favorites = [] } = useGetSnapshotViewFavoritesQuery();
  const [addFavorite, { isLoading: isAdding }] = useAddSnapshotViewFavoriteMutation();
  const [deleteFavorite, { isLoading: isDeleting }] = useDeleteSnapshotViewFavoriteMutation();
  const isPinned = favorites.some(favorite => favorite.id === view.id);
  const isLoading = isPinned ? isDeleting : isAdding;
  const action = isPinned ? "Unpin" : "Pin";

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      if (isPinned) {
        await deleteFavorite(view.id).unwrap();
        toast.success("View unpinned from sidebar");
      } else {
        await addFavorite(view).unwrap();
        toast.success("View pinned to sidebar");
      }
    } catch {
      toast.error("Unable to update View pin");
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size={compact ? "xs" : "icon"}
      className={clsx("text-muted-foreground hover:text-coral", { "text-coral": isPinned })}
      aria-label={`${action} ${view.name} ${isPinned ? "from" : "to"} sidebar`}
      aria-pressed={isPinned}
      title={`${action} ${view.name} ${isPinned ? "from" : "to"} sidebar`}
      disabled={isLoading}
      onClick={handleClick}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <Pin className={clsx("h-4 w-4", { "rotate-45 fill-current": isPinned })} aria-hidden="true" />
      )}
    </Button>
  );
};
